import React from "react";
import { Link } from "react-router-dom";
import RestrictedPill from "../../../components/RestrictedPill";
import ScrollableTableWrapper from "../../../components/ScrollableTableWrapper";
import type {
  InvestorStrategyRecord,
  StrategyEntry,
} from "../../../services/investorStrategyService";
import type { IbkrAccountSummary } from "../../../types";
import {
  computeDrift,
  getStrategyDisplayName,
  getStrategyViewUrl,
  type ProductLookupEntry,
  type ProductLookupKey,
} from "../../../utils/strategyUtils";
import { formatCurrencyWithCommas, formatPercentage } from "../../../utils/numberUtils";
import type { StrategyTotals } from "../../../utils/strategyTotals";
import type { StrategySinceAddedByProduct } from "../../../hooks/useStrategySinceAddedReturns";
import { DriftCell, PositionCountCell, ReturnCell, SinceAddedCell } from "./StrategyTableCells";
import { AvailableCapacityDollarsCell, AvailableCapacityPctCell } from "./StrategyTotalsCells";
import { PIE_COLORS } from "./StrategyDonutCard";

interface Props {
  // Every held record, so an admin surface can render a managed strategy as an
  // ordinary row. Investor callers pass StrategyEntry[] and are unaffected.
  strategies: InvestorStrategyRecord[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  positionCountsMap: Map<string, { long: number; short: number; options: number }>;
  sinceAddedByProduct: StrategySinceAddedByProduct;
  accountSummary: IbkrAccountSummary | null;
  totals: StrategyTotals;
  footerActionRow?: React.ReactNode;
  /** True when this holding's product carries contracts the account cannot trade. */
  isEntryRestricted?: (entry: StrategyEntry) => boolean;
  onRestrictedClick?: (entry: StrategyEntry) => void;
  appearance?: "default" | "alpha";
}

export function StrategyDesktopTable({
  strategies,
  productLookup,
  positionCountsMap,
  sinceAddedByProduct,
  accountSummary,
  totals,
  footerActionRow,
  isEntryRestricted,
  onRestrictedClick,
  appearance = "default",
}: Props) {
  const isAlpha = appearance === "alpha";
  const headerTextClass = isAlpha ? "text-v7-ink-soft" : "text-gray-500";
  const totalValueClass = isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white";
  return (
    <ScrollableTableWrapper>
      <table className={`w-full min-w-[820px] ${isAlpha ? "text-[13px] text-v7-ink" : ""}`}>
        <thead>
          <tr className={isAlpha ? "border-b border-v7-ink/15 bg-v7-paper" : "border-b border-gray-200 dark:border-rp-separator"}>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 w-16 ${headerTextClass}`}>ID</th>
            <th className={`text-[11px] uppercase tracking-wider text-left px-4 py-3 ${headerTextClass}`}>Product</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-3 py-3 w-14 ${headerTextClass}`}>Pos</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Today</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Since Added</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Allocated (%)</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Allocated ($)</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Current (%)</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Current ($)</th>
            <th className={`text-[11px] uppercase tracking-wider text-right px-4 py-3 ${headerTextClass}`}>Drift</th>
          </tr>
        </thead>
        <tbody>
          {strategies.map((entry, idx) => {
            const name = getStrategyDisplayName(entry, productLookup);
            const viewUrl = getStrategyViewUrl(entry, productLookup);
            const drift = computeDrift(entry);
            const color = PIE_COLORS[idx % PIE_COLORS.length];
            const productKey = `${entry.entityType}-${entry.productId}` as ProductLookupKey;
            const counts = positionCountsMap.get(productKey);
            const sinceAdded = sinceAddedByProduct[productKey] ?? null;
            const returnToday = sinceAdded?.todayReturnPct ?? null;
            // Restriction flags are a catalogue-product concept; a managed
            // strategy has no product row to carry them, so it is never marked.
            const restrictableEntry: StrategyEntry | null =
              entry.entityType === "MANAGED_STRATEGY" ? null : entry;
            const isRestricted = restrictableEntry
              ? isEntryRestricted?.(restrictableEntry) ?? false
              : false;
            const currentWeight = entry.driftedWeight ?? parseFloat(entry.weight);

            return (
              <tr
                key={`${entry.entityType}-${entry.productId}`}
                className={`border-b transition-colors last:border-b-0 ${isAlpha
                  ? "border-v7-ink/10 hover:bg-v7-paper"
                  : "border-gray-100 hover:bg-gray-100 dark:border-rp-separator dark:hover:bg-white/[0.02]"}`}
              >
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-400 dark:text-gray-500"}`}>{entry.productId}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    {viewUrl ? (
                      <Link to={viewUrl} className={`truncate text-sm font-medium hover:underline ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{name}</Link>
                    ) : (
                      <span className={`truncate text-sm font-medium ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{name}</span>
                    )}
                    {/* Sibling of the Link, never inside it: an anchor must not
                        contain a button. */}
                    {isRestricted && (
                      <RestrictedPill
                        appearance={appearance}
                        onClick={() => restrictableEntry && onRestrictedClick?.(restrictableEntry)}
                      />
                    )}
                  </div>
                </td>
                <td className={`w-14 px-3 py-2.5 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                  <PositionCountCell counts={counts ?? null} appearance={appearance} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm">
                  <ReturnCell value={returnToday} appearance={appearance} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm">
                  <SinceAddedCell value={sinceAdded} appearance={appearance} />
                </td>
                <td className={`px-4 py-2.5 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{formatPercentage(parseFloat(entry.weight))}</td>
                <td className={`px-4 py-2.5 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                  {accountSummary
                    ? formatCurrencyWithCommas((parseFloat(entry.weight) / 100) * accountSummary.netLiquidationSecurity, 0)
                    : "--"}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                  {formatPercentage(currentWeight)}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                  {accountSummary
                    ? formatCurrencyWithCommas((currentWeight / 100) * accountSummary.netLiquidationSecurity, 0)
                    : "--"}
                </td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm"><DriftCell drift={drift} appearance={appearance} /></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {strategies.length > 0 && (
            <>
              <tr className={isAlpha ? "border-t-2 border-v7-ink/15 bg-v7-paper" : "border-t-2 border-gray-200 bg-gray-50/60 dark:border-rp-separator dark:bg-white/[0.02]"}>
                <td colSpan={5} className={`px-4 py-2.5 text-sm font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>Total</td>
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm font-semibold ${totalValueClass}`}>{formatPercentage(totals.allocatedPct)}</td>
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm font-semibold ${totalValueClass}`}>
                  {totals.allocatedDollars != null ? formatCurrencyWithCommas(totals.allocatedDollars, 0) : "--"}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm font-semibold ${totalValueClass}`}>{formatPercentage(totals.currentPct)}</td>
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm font-semibold ${totalValueClass}`}>
                  {totals.currentDollars != null ? formatCurrencyWithCommas(totals.currentDollars, 0) : "--"}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}`}>--</td>
              </tr>
              <tr className={`border-t ${isAlpha ? "border-v7-ink/10" : "border-gray-100 dark:border-rp-separator"}`}>
                <td colSpan={5} className={`px-4 py-2.5 text-sm ${isAlpha ? "text-v7-ink" : "text-gray-700 dark:text-gray-300"}`}>
                  Available capacity <span className={`text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>(cap {formatPercentage(totals.capacityPct)})</span>
                </td>
                <td className="px-4 py-2.5" />
                <td className="px-4 py-2.5" />
                <AvailableCapacityPctCell value={totals.remainingCurrentPct} appearance={appearance} />
                <AvailableCapacityDollarsCell value={totals.remainingCurrentDollars} appearance={appearance} />
                <td className="px-4 py-2.5" />
              </tr>
            </>
          )}
          {footerActionRow}
        </tfoot>
      </table>
    </ScrollableTableWrapper>
  );
}
